import { useFacturationService } from '@europrocurement/l2d-domain';
import {
    ApiOpensearchFacturesGetCollection200Response,
    FactureOpenSearchJsonldOpensearchFactureRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { AxiosResponse } from 'axios';

export type FactureType = FactureOpenSearchJsonldOpensearchFactureRead & Record<string, unknown>;

const promiseTimeoutWrapper = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    apiPromise: Promise<AxiosResponse<ApiOpensearchFacturesGetCollection200Response, any>>,
): Promise<Array<FactureType>> => {
    let searchCollectionTimeout: number | undefined;

    return new Promise((resolve, reject) => {
        clearTimeout(searchCollectionTimeout);
        searchCollectionTimeout = setTimeout(() => {
            apiPromise
                .then((res) => resolve(res.data['hydra:member'] as FactureType[]))
                .catch(() => reject());
        }, 300);
    });
};

export type LimitAccessFilterApiMultipleFactureOpenSearch = {
    prescriberIdRef?: React.MutableRefObject<number | undefined>;
    ddmIdFilter?: number;
};

const useFactureApiHook = ({
    prescriberIdRef,
    ddmIdFilter,
}: LimitAccessFilterApiMultipleFactureOpenSearch) => {
    const { facturesOpenSearchApi } = useFacturationService();

    let prescripteurId: number | undefined;
    let ddmIds: Array<number> = [];

    if (prescriberIdRef?.current !== undefined) {
        prescripteurId = prescriberIdRef.current as number;
    }
    if (ddmIdFilter) {
        ddmIds = [...ddmIds, ddmIdFilter as number];
    }

    const searchFactureSocieteFacturee = (term: string | undefined): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesClientsGetCollection({
                q: term,
                prescripteurId,
                ddmId: ddmIds,
            }),
        );

    const searchFactureSocieteOperee = (term: string | undefined): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesClientsOperesGetCollection({
                q: term,
                prescripteurId,
                ddmId: ddmIds,
            }),
        );

    const searchFactureDossier = (term: string | undefined): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesDossiersGetCollection({
                q: term,
                prescripteurId,
                ddmId: ddmIds,
            }),
        );

    const searchFactureNumero = (term: string | undefined): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesCollectionGetCollection({
                q: term,
                prescripteurId,
                ddmId: ddmIds,
            }),
        );

    const searchFactureReference = (term: string | undefined): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesReferencesGetCollection({
                q: term,
                prescripteurId,
                ddmId: ddmIds,
            }),
        );

    // Fetch and search domains linked to open search invoices
    const searchFactureDomain = (term: string | undefined): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesDomainesGetCollection({
                q: term,
                prescripteurId,
            }),
        );
    // Filter to change domains
    // {
    //     label: 'Domaines',
    //     field: 'ddmId',
    //     type: 'selectApi',
    //     searchFunction: searchFactureDomain,
    //     renderItem: (domain: FactureType) => domain.libelle as string,
    //     valueMap: (domain: FactureType) => domain.id as number,
    // },

    // Fetch and search prescribers linked to open search invoices
    const searchFacturePrescriber = (term: string | undefined): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesPrescripteursGetCollection({
                q: term,
                ddmId: ddmIds,
            }),
        );

    const searchFactureCollectionDossier = (
        term: string | undefined,
    ): Promise<Array<FactureType>> =>
        promiseTimeoutWrapper(
            facturesOpenSearchApi.apiOpensearchFacturesDossiersCollectionCommandeGetCollection({
                q: term,
                ddmId: ddmIds,
                prescripteurId,
            }),
        );

    return {
        searchFactureSocieteFacturee,
        searchFactureSocieteOperee,
        searchFactureDossier,
        searchFactureNumero,
        searchFactureReference,
        searchFactureDomain,
        searchFacturePrescriber,
        searchFactureCollectionDossier,
    };
};

export default useFactureApiHook;
